/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        components/mobile/OfflineIndicator.tsx
 * @description Bandeau visuel indiquant le mode hors ligne et les actions en attente
 *              S'affiche en haut de l'ecran quand l'appareil est offline
 *              ou quand des actions sont en attente de synchronisation
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useOfflineSync } from '@/hooks/useOfflineSync';
import {
  WifiIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { WifiIcon as WifiSolidIcon } from '@heroicons/react/24/solid';

interface OfflineIndicatorProps {
  /** Additional CSS classes */
  className?: string;
  /** Show even when online with 0 pending actions (for debugging) */
  alwaysShow?: boolean;
}

export function OfflineIndicator({ className = '', alwaysShow = false }: OfflineIndicatorProps) {
  const { isOnline, pendingCount, isSyncing, sync, lastSyncResult } = useOfflineSync();

  // Don't show if online with no pending actions (unless alwaysShow)
  if (isOnline && pendingCount === 0 && !alwaysShow) {
    return null;
  }

  // Determine banner style based on state
  const getBannerStyle = () => {
    if (!isOnline) {
      return 'bg-amber-500 text-white';
    }
    if (isSyncing) {
      return 'bg-blue-500 text-white';
    }
    if (pendingCount > 0) {
      return 'bg-orange-500 text-white';
    }
    if (lastSyncResult && !lastSyncResult.success) {
      return 'bg-red-500 text-white';
    }
    return 'bg-green-500 text-white';
  };

  // Determine icon based on state
  const getIcon = () => {
    if (!isOnline) {
      return <WifiIcon className="h-5 w-5" />;
    }
    if (isSyncing) {
      return <ArrowPathIcon className="h-5 w-5 animate-spin" />;
    }
    if (pendingCount > 0) {
      return <CloudArrowUpIcon className="h-5 w-5" />;
    }
    if (lastSyncResult && !lastSyncResult.success) {
      return <ExclamationTriangleIcon className="h-5 w-5" />;
    }
    return <WifiSolidIcon className="h-5 w-5" />;
  };

  // Determine message based on state
  const getMessage = () => {
    if (!isOnline) {
      if (pendingCount > 0) {
        return `Mode hors ligne - ${pendingCount} action${pendingCount > 1 ? 's' : ''} en attente`;
      }
      return 'Mode hors ligne';
    }
    if (isSyncing) {
      return 'Synchronisation en cours...';
    }
    if (pendingCount > 0) {
      return `${pendingCount} action${pendingCount > 1 ? 's' : ''} en attente de sync`;
    }
    if (lastSyncResult && !lastSyncResult.success) {
      return `Erreur de sync (${lastSyncResult.failed} echec${lastSyncResult.failed > 1 ? 's' : ''})`;
    }
    return 'En ligne';
  };

  const handleSyncClick = async () => {
    if (isOnline && !isSyncing && pendingCount > 0) {
      await sync();
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 ${getBannerStyle()} ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          {getIcon()}
          <span className="text-sm font-medium">{getMessage()}</span>
        </div>

        {/* Sync button (only when online with pending actions) */}
        {isOnline && pendingCount > 0 && !isSyncing && (
          <button
            onClick={handleSyncClick}
            className="flex items-center gap-1 rounded-md bg-white/20 px-2 py-1 text-xs font-medium hover:bg-white/30 active:bg-white/40 transition-colors"
            aria-label="Synchroniser maintenant"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Sync
          </button>
        )}

        {/* Success indicator after sync */}
        {lastSyncResult?.success && lastSyncResult.synced > 0 && isOnline && pendingCount === 0 && (
          <div className="flex items-center gap-1 text-xs">
            <CheckCircleIcon className="h-4 w-4" />
            <span>{lastSyncResult.synced} synchronise{lastSyncResult.synced > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact version of the offline indicator (just an icon)
 * Useful for status bars
 */
export function OfflineStatusIcon({ className = '' }: { className?: string }) {
  const { isOnline, pendingCount, isSyncing } = useOfflineSync();

  if (isOnline && pendingCount === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {!isOnline ? (
        <WifiIcon className="h-5 w-5 text-amber-500" />
      ) : isSyncing ? (
        <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />
      ) : (
        <CloudArrowUpIcon className="h-5 w-5 text-orange-500" />
      )}

      {/* Badge for pending count */}
      {pendingCount > 0 && (
        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
          {pendingCount > 9 ? '9+' : pendingCount}
        </span>
      )}
    </div>
  );
}

export default OfflineIndicator;
